import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { CalWebSocketService, CalWebSocketMessage } from '../services/calWebSocketService';

interface CalWebSocketContextType {
  sendMessage: (message: string, context?: any, sessionIdOverride?: string) => void;
  isConnected: boolean;
  addMessageHandler: (handler: (message: CalWebSocketMessage) => void) => () => void;
}

const CalWebSocketContext = createContext<CalWebSocketContextType | null>(null);

export const CalWebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, getIdTokenClaims } = useAuth0();
  const [isConnected, setIsConnected] = useState(false);
  const wsServiceRef = useRef<CalWebSocketService | null>(null);
  const messageHandlersRef = useRef<Set<(message: CalWebSocketMessage) => void>>(new Set());

  // Initialize WebSocket connection once for the entire app
  useEffect(() => {
    const initWebSocket = async () => {
      if (!user) return;

      try {
        // Get Auth0 ID token for WebSocket authentication
        const tokenClaims = await getIdTokenClaims();
        const idToken = tokenClaims?.__raw; // Raw JWT token

        if (!idToken) {
          console.warn('No ID token available for WebSocket');
          return;
        }

        // Create WebSocket service
        wsServiceRef.current = new CalWebSocketService({
          token: idToken,
          onMessage: (data) => {
            // Notify all registered handlers
            messageHandlersRef.current.forEach(handler => handler(data));
          },
          onConnect: () => {
            console.log('Shared Cal WebSocket connected');
            setIsConnected(true);
          },
          onDisconnect: () => {
            console.log('Shared Cal WebSocket disconnected');
            setIsConnected(false);
          },
          onError: (err) => {
            console.error('Shared Cal WebSocket error:', err);
            setIsConnected(false);
          }
        });

        // Connect to WebSocket
        await wsServiceRef.current.connect();
      } catch (err) {
        console.error('Failed to initialize shared WebSocket:', err);
      }
    };

    initWebSocket();

    // Cleanup on unmount
    return () => {
      if (wsServiceRef.current) {
        wsServiceRef.current.disconnect();
        wsServiceRef.current = null;
      }
    };
  }, [user, getIdTokenClaims]);

  const sendMessage = useCallback((message: string, context?: any, sessionIdOverride?: string) => {
    if (wsServiceRef.current && isConnected) {
      wsServiceRef.current.sendMessage(message, context, sessionIdOverride);
    } else {
      console.warn('WebSocket not connected, message not sent');
    }
  }, [isConnected]);

  const addMessageHandler = useCallback((handler: (message: CalWebSocketMessage) => void) => {
    messageHandlersRef.current.add(handler);

    // Return cleanup function
    return () => {
      messageHandlersRef.current.delete(handler);
    };
  }, []);

  const value: CalWebSocketContextType = {
    sendMessage,
    isConnected,
    addMessageHandler
  };

  return (
    <CalWebSocketContext.Provider value={value}>
      {children}
    </CalWebSocketContext.Provider>
  );
};

export const useCalWebSocket = () => {
  const context = useContext(CalWebSocketContext);
  if (!context) {
    throw new Error('useCalWebSocket must be used within CalWebSocketProvider');
  }
  return context;
};